import * as React from 'react'
import styled from 'styled-components'
import * as constants from '../../static/constants'
import InputLabel from './InputLabel'
import ClassNames from 'classnames'
import SvgIcon from 'components/atoms/SvgIcon'

interface IProps {
  name: string
  defaultChecked?: boolean
  value?: string | number
  label?: string
  showLabelInline?: boolean
  error?: null | string
  singleCheckBox?: boolean
  readonly?: boolean
  checked?: boolean
  disabled?: boolean
  onChangeHandler?(event: any): void
}

const CheckBox: React.FC<IProps> = props => {
  return (
    <CheckBoxWrapper className="CheckBox" singleCheckBox={props.singleCheckBox}>
      <div className="CheckBox_Wrapper">
        {props.label && !props.showLabelInline && <InputLabel label={props.label} />}
        <div className="CheckBox_Main">
          <label
            className={ClassNames({
              readonly: props.readonly,
              disabled: props.disabled,
            })}
          >
            <input
              id={props.name}
              type="checkbox"
              name={props.name}
              value={props.value}
              checked={props.checked}
              defaultChecked={props.checked === undefined ? props.defaultChecked : undefined}
              disabled={props.disabled}
              onChange={event => {
                if (
                  typeof props.onChangeHandler === 'function' &&
                  !props.readonly &&
                  !props.disabled
                ) {
                  props.onChangeHandler(event)
                }
              }}
            />
            <span className="CheckBox_Icon">
              <SvgIcon
                name="check_box"
                className="icon filled"
                ariaHidden={true}
                role="presentation"
              />
              <SvgIcon
                name="check_box_outline_blank"
                className="icon outline"
                ariaHidden={true}
                role="presentation"
              />
            </span>
            {props.showLabelInline && <span>{props.label}</span>}
          </label>
        </div>
      </div>
      {props.error && <span className="CheckBox_Error">{props.error}</span>}
    </CheckBoxWrapper>
  )
}

const CheckBoxWrapper = styled.div`
  ${({ singleCheckBox }) => singleCheckBox && `display: flex;`}
  .CheckBox_Wrapper {
    display: flex;
  }

  .CheckBox_Main {
    display: flex;
    align-items: center;
    flex: 1;

    > label {
      display: flex;
      align-items: center;
      position: relative;
      cursor: pointer;
      font-size: 14px;
      font-weight: 700;

      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        font-size: 12px;
      }
      &:hover {
        opacity: 0.75;
      }
      &.readonly,
      &.disabled {
        pointer-events: none;
        color: ${constants.COLORS.disabled};
      }

      input {
        opacity: 0;
        height: 0;
        width: 0;
      }

      .CheckBox_Icon {
        margin-right: 8px;
        font-size: 22px;
        @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
          font-size: 18px;
          ${({ singleCheckBox }) => singleCheckBox && `margin-bottom: 12px;`}
        }
        .icon {
          &.filled {
            display: none;
            color: ${constants.COLORS.primary};
          }

          &.outline {
            display: block;
            color: ${constants.COLORS.divider};
          }
        }
      }

      &.disabled .CheckBox_Icon {
        .icon {
          color: ${constants.COLORS.disabled};
        }
      }

      input:checked + .CheckBox_Icon {
        .icon {
          &.filled {
            display: block;
          }

          &.outline {
            display: none;
          }
        }
      }

      span {
        font-size: 14px;
      }
    }
  }

  .CheckBox_Error {
    display: block;
    margin-top: 8px;
    color: ${constants.COLORS.cautionText};
    font-size: 14px;
  }

  & + & {
    margin-left: 22px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      margin-left: 0;
      margin-top: 8px;
    }
  }
`

export default CheckBox
